import { type ReactNode } from "react";
import { Divider, Stack, useTheme } from "@mui/material";
import { FoodsIconButton, FoodsTags, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import type { AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";
import { ActionIndicator } from "./ActionIndicator";
import { ActorPictogram } from "./ActorBadge";
import { AuditLogChanges } from "./AuditLogChanges";
import { AuditLogCopyField } from "./AuditLogCopyField";
import { AuditLogFilterLink } from "./AuditLogFilterLink";
import { auditLogRecipient, auditLogSubject } from "./auditLogPresentation";
import type { AuditLogQueryFilters } from "./auditLogQuery";
import { useAuditLogLabels } from "./useAuditLogLabels";

type AuditLogDetailProps = {
    entry: AuditLogEntry;
    isStacked: boolean;
    onFocusFilters: (filters: AuditLogQueryFilters) => void;
    onViewObjectHistory: (filters: AuditLogQueryFilters) => void;
};

const impersonatedBy = (entry: AuditLogEntry): string | null => {
    const value = entry.metadata?.["impersonatedBy"];

    return typeof value === "string" && value !== "" ? value : null;
};

export const AuditLogDetail = (props: AuditLogDetailProps) => {
    const { entry, isStacked, onFocusFilters, onViewObjectHistory } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const labels = useAuditLogLabels();
    const supervisedBy = impersonatedBy(entry);
    const typeLabel = labels.entityType(entry.entityType);
    const subject = auditLogSubject(entry);
    const recipient = auditLogRecipient(entry);

    return (
        <Stack gap={theme.spacing(3)}>
            <Stack gap={theme.spacing(2)}>
                <Stack gap={theme.spacing(0.5)}>
                    <ActionIndicator action={entry.action} />
                    <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                        {subject ?? typeLabel}
                    </Typography>
                    <Typography variant="small-regular" color="text.secondary">
                        {typeLabel}
                    </Typography>
                </Stack>

                <Stack gap={theme.spacing(1)}>
                    {recipient !== null && (
                        <Field label={t("settings.audit_log.field.recipient", "Recipient")}>
                            <AuditLogFilterLink
                                label={recipient}
                                tooltip={t("settings.audit_log.focus.recipient", "Filter the log on this recipient")}
                                onClick={() => onFocusFilters({ q: recipient })}
                            />
                            <Typography variant="small-regular" color="text.secondary">
                                {labels.recipientHelper(entry.action)}
                            </Typography>
                        </Field>
                    )}

                    <Field label={t("settings.audit_log.field.actor", "Actor")}>
                        <Stack direction="row" gap={theme.spacing(1)} alignItems="center" flexWrap="wrap">
                            <ActorPictogram entry={entry} />
                            {entry.actorId === null ?
                                <Typography variant="body-semibold" sx={{ wordBreak: "break-all" }}>
                                    {entry.actorLabel ?? t("settings.audit_log.actor.unknown", "Unknown actor")}
                                </Typography>
                            :   <AuditLogFilterLink
                                    label={entry.actorLabel ?? t("settings.audit_log.actor.unknown", "Unknown actor")}
                                    tooltip={t(
                                        "settings.audit_log.focus.actor",
                                        "Add this actor to the filters and facets",
                                    )}
                                    onClick={() =>
                                        onFocusFilters({
                                            actorId: entry.actorId === null ? undefined : [entry.actorId],
                                        })
                                    }
                                />
                            }
                            <Typography variant="small-regular" color="text.secondary">
                                {labels.actorType(entry.actorType)}
                            </Typography>
                            {supervisedBy !== null && (
                                <FoodsTags
                                    variant="tags-coral"
                                    size="display"
                                    label={t("settings.audit_log.field.impersonation_chip", "Impersonation")}
                                />
                            )}
                        </Stack>
                        {supervisedBy !== null && (
                            <Typography variant="small-regular" color="text.secondary">
                                {t(
                                    "settings.audit_log.field.impersonation",
                                    "{{actor}} acted as {{account}}. The entry stays attributed to the real account.",
                                    {
                                        actor: entry.actorLabel ?? "",
                                        account: supervisedBy,
                                    },
                                )}
                            </Typography>
                        )}
                    </Field>

                    <Field label={t("settings.audit_log.field.occurred_at", "Timestamp")}>
                        <Typography variant="body-medium">{labels.utcTimestamp(entry.occurredAt)}</Typography>
                        <Typography variant="small-regular" color="text.secondary">
                            {labels.relativeTime(entry.occurredAt)}
                        </Typography>
                    </Field>
                </Stack>
            </Stack>

            <Divider />

            <Stack gap={theme.spacing(1)}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" gap={theme.spacing(1)}>
                    <Typography variant="h5">{t("settings.audit_log.section.changes", "Changes")}</Typography>
                    {entry.entityId !== "" && (
                        <FoodsIconButton
                            icon="history"
                            size="s"
                            tooltip={{
                                text: t("settings.audit_log.focus.object", "All history for this object"),
                            }}
                            buttonProps={{
                                onClick: () =>
                                    onViewObjectHistory({ entityType: [entry.entityType], entityId: entry.entityId }),
                            }}
                        />
                    )}
                </Stack>
                <Typography variant="small-regular" color="text.secondary">
                    {t("settings.audit_log.section.changes_on", "Fields changed on: {{type}}", {
                        type: typeLabel,
                    })}
                </Typography>
                <AuditLogChanges entry={entry} isStacked={isStacked} />
            </Stack>

            <Divider />

            <Stack gap={theme.spacing(1)}>
                <Typography variant="h5">{t("settings.audit_log.section.traceability", "Traceability")}</Typography>

                <Field label={t("settings.audit_log.field.scope", "Scope")}>
                    <Typography variant="body-medium">{entry.rootLabel ?? labels.scopeType(entry.rootType)}</Typography>
                    {entry.rootId === null && (
                        <Typography variant="small-regular" color="text.secondary">
                            {t("settings.audit_log.field.scope_none", "No owning organization")}
                        </Typography>
                    )}
                </Field>

                <Field label={t("settings.audit_log.field.ip", "IP address")}>
                    {entry.ip === null ?
                        <Typography variant="small-regular" color="text.secondary">
                            {t("settings.audit_log.field.ip_absent", "not recorded")}
                        </Typography>
                    :   <Typography variant="body-medium">{entry.ip}</Typography>}
                </Field>

                <Field
                    label={t("settings.audit_log.field.request_id", "Application request")}
                    helper={
                        entry.requestId === null ?
                            t(
                                "settings.audit_log.field.request_id_absent_helper",
                                "No X-Request-Id header — no correlation is possible.",
                            )
                        :   undefined
                    }
                >
                    <AuditLogCopyField
                        value={entry.requestId}
                        absentLabel={t("settings.audit_log.field.request_id_absent", "not sent by the client")}
                    />
                </Field>

                <Field label={t("settings.audit_log.field.entity_id", "Object identifier")}>
                    <AuditLogCopyField
                        value={entry.entityId === "" ? null : entry.entityId}
                        absentLabel={t(
                            "settings.audit_log.field.entity_id_absent",
                            "not yet assigned when the object was created",
                        )}
                    />
                </Field>

                <Field label={t("settings.audit_log.field.entry_id", "Entry identifier")}>
                    <AuditLogCopyField value={entry.id} absentLabel="" />
                </Field>
            </Stack>
        </Stack>
    );
};

type FieldProps = {
    label: string;
    helper?: string;
    children: ReactNode;
};

const Field = (props: FieldProps) => {
    const { label, helper, children } = props;
    const theme = useTheme();

    return (
        <Stack gap={theme.spacing(0.25)}>
            <Typography variant="small-regular" color="text.secondary">
                {label}
            </Typography>
            {children}
            {helper !== undefined && (
                <Typography variant="small-regular" color="text.secondary">
                    {helper}
                </Typography>
            )}
        </Stack>
    );
};
